import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Bold,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Code,
  Eye,
  EyeOff,
  Heading,
  Italic,
  LayoutGrid,
  RotateCcw,
  List,
  ListOrdered,
  Maximize2,
  Minus,
  Pin,
  PinOff,
  Plus,
  StickyNote as StickyIcon,
  Strikethrough,
  Trash2,
  Underline,
  Undo2,
  X,
} from "lucide-react";
import useProximityRaise, { NOTE_DRAG_CLASS } from "../hooks/useProximityRaise";
import {
  renderNoteMarkdown,
  toggleCheckboxLine,
  wrapSelection,
  applyLinePrefix,
} from "../utils/noteMarkdown";

// Notes look like paper in every skin/theme, so colours are literal (not the
// remapped Tailwind palette).
export const NOTE_COLORS = {
  yellow: { bg: "#fef3c7", border: "#f59e0b", text: "#78350f", dot: "#fbbf24" },
  pink: { bg: "#fce7f3", border: "#ec4899", text: "#831843", dot: "#f472b6" },
  blue: { bg: "#dbeafe", border: "#3b82f6", text: "#1e3a8a", dot: "#60a5fa" },
  green: { bg: "#dcfce7", border: "#22c55e", text: "#14532d", dot: "#4ade80" },
  purple: { bg: "#ede9fe", border: "#8b5cf6", text: "#4c1d95", dot: "#a78bfa" },
};
const COLOR_KEYS = Object.keys(NOTE_COLORS);
const colorOf = (c) => NOTE_COLORS[c] || NOTE_COLORS.yellow;

const MIN_W = 160;
const MIN_H = 110;
const HEADER_H = 30;
const FOOTER_H = 30;
const TOOLBAR_H = 28;
const EDGE = 8;

const panelEls = () =>
  Array.from(document.querySelectorAll("[data-panel-section][data-panel-key]"));
// Notes on the Sticky Notes board anchor to its dashed grid area (not the
// panel chrome), so 0,0 is the top-left corner of the grid.
export const BOARD_KEY = "stickyNotes";
const panelEl = (key) => {
  if (!key) return null;
  if (key === BOARD_KEY) {
    const board = document.querySelector("[data-notes-board]");
    if (board) return board;
  }
  return document.querySelector(
    `[data-panel-section][data-panel-key="${key}"]`,
  );
};
// ---------------------------------------------------------------------------
// Board grid. Notes parked on the Sticky Notes board live on a fixed grid so
// they can never overlap and never leave the dashed area: a note occupies a
// whole number of cells, and every position is a cell corner.
// ---------------------------------------------------------------------------
export const BOARD_CARD_W = 228;
export const BOARD_CARD_H = 170;
export const BOARD_GAP = 16;

const cellX = (col) => BOARD_GAP + col * (BOARD_CARD_W + BOARD_GAP);
const cellY = (row) => BOARD_GAP + row * (BOARD_CARD_H + BOARD_GAP);
const colAt = (x) => Math.round((x - BOARD_GAP) / (BOARD_CARD_W + BOARD_GAP));
const rowAt = (y) => Math.round((y - BOARD_GAP) / (BOARD_CARD_H + BOARD_GAP));
const spanCols = (w) =>
  Math.max(1, Math.ceil((Number(w) || BOARD_CARD_W) / (BOARD_CARD_W + BOARD_GAP)));
const spanRows = (h) =>
  Math.max(1, Math.ceil((Number(h) || BOARD_CARD_H) / (BOARD_CARD_H + BOARD_GAP)));

/** The dashed grid element, if the board panel is on screen and expanded. */
export const boardEl = () => document.querySelector("[data-notes-board]");

/** How many grid columns fit in the board (falls back to a sane default). */
export function boardColumns(width) {
  const el = boardEl();
  const w = Number.isFinite(width) ? width : el ? el.clientWidth : 960;
  return Math.max(1, Math.floor((w - BOARD_GAP) / (BOARD_CARD_W + BOARD_GAP)));
}

/** Widest a note may be on a board this many columns wide. */
export const boardMaxNoteW = (cols) =>
  cols * BOARD_CARD_W + (cols - 1) * BOARD_GAP;

const cellRect = (n) => {
  const col = Math.max(0, colAt(n.x || 0));
  const row = Math.max(0, rowAt(n.y || 0));
  return {
    col,
    row,
    cw: spanCols(n.w),
    ch: spanRows(n.minimized ? 32 : n.h),
  };
};
const hits = (a, b) =>
  a.col < b.col + b.cw &&
  a.col + a.cw > b.col &&
  a.row < b.row + b.ch &&
  a.row + a.ch > b.row;

/**
 * Nearest free cell for a note spanning cw x ch cells, searching outward from
 * wanted cell. `taken` are the other board notes. Always returns a position
 * inside the board (row grows downwards; the board grows with it).
 */
export function freeBoardCell(wantCol, wantRow, cw, ch, taken, cols) {
  const maxCol = Math.max(0, cols - cw);
  const start = {
    col: Math.min(Math.max(0, Math.round(wantCol)), maxCol),
    row: Math.max(0, Math.round(wantRow)),
  };
  const busy = (taken || []).map(cellRect);
  const fits = (col, row) => !busy.some((b) => hits({ col, row, cw, ch }, b));
  if (fits(start.col, start.row)) return start;
  const maxRow =
    busy.reduce((m, b) => Math.max(m, b.row + b.ch), 0) + ch + 1;
  let best = null;
  let bestD = Infinity;
  for (let row = 0; row <= maxRow; row++) {
    for (let col = 0; col <= maxCol; col++) {
      if (!fits(col, row)) continue;
      const d = Math.hypot(col - start.col, (row - start.row) * 1.35);
      if (d < bestD) {
        bestD = d;
        best = { col, row };
      }
    }
  }
  return best || { col: 0, row: maxRow };
}

/** Grid-snapped board position (x/y in board-local px) for a note. */
export function snapToBoard(note, others, cols, wantX, wantY) {
  const columns = cols || boardColumns();
  const w = Math.min(
    Number(note.w) || BOARD_CARD_W,
    boardMaxNoteW(columns),
  );
  const h = note.minimized ? 32 : Number(note.h) || BOARD_CARD_H;
  const cw = Math.min(spanCols(w), columns);
  const ch = spanRows(h);
  const cell = freeBoardCell(
    colAt(wantX === undefined ? note.x || 0 : wantX),
    rowAt(wantY === undefined ? note.y || 0 : wantY),
    cw,
    ch,
    (others || []).filter((o) => o.id !== note.id),
    columns,
  );
  return { x: cellX(cell.col), y: cellY(cell.row), w: Math.round(w) };
}

// Fired when a panel moves or resizes: every pinned note re-reads its anchor
// so it keeps sitting exactly on the panel it belongs to (the page settles
// after fonts load, panels expand, other panels grow...).
const RELAYOUT_EVENT = "xz-notes-relayout";
const requestRelayout = (() => {
  let queued = false;
  return () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      try {
        window.dispatchEvent(new CustomEvent(RELAYOUT_EVENT));
      } catch (e) {
        /* ignore */
      }
    });
  };
})();

// Let the board (and anything else) know a note moved/resized live.
const GEOMETRY_EVENT = "xz-notes-geometry";
const announceGeometry = () => {
  try {
    window.dispatchEvent(new CustomEvent(GEOMETRY_EVENT));
  } catch (e) {
    /* ignore */
  }
};
const docWidth = () => Math.max(document.documentElement.clientWidth || 0, 320);
const docHeight = () =>
  Math.max(document.documentElement.scrollHeight || 0, window.innerHeight || 0);

// Notes may never sit above the page content (over the header/progress strip)
// or off the sides. This is the single source of truth for that rule.
const contentTop = () => {
  const main = document.querySelector("main");
  return main
    ? Math.round(main.getBoundingClientRect().top + window.scrollY)
    : 0;
};
function clampPage(left, top, w, h) {
  const maxLeft = Math.max(EDGE, docWidth() - (w || 220) - EDGE);
  const minTop = contentTop() + EDGE;
  const maxTop = Math.max(minTop, docHeight() - (h || 160) - EDGE);
  return {
    left: Math.min(Math.max(EDGE, left), maxLeft),
    top: Math.min(Math.max(minTop, top), maxTop),
  };
}

const viewWidth = () =>
  Math.max(document.documentElement.clientWidth || 0, 320);
const viewHeight = () =>
  Math.max(
    window.innerHeight || document.documentElement.clientHeight || 0,
    200,
  );

// Floating notes are position:fixed, so they must stay fully inside the window.
function clampView(left, top, w, h) {
  const maxLeft = Math.max(EDGE, viewWidth() - (w || 220) - EDGE);
  const maxTop = Math.max(EDGE, viewHeight() - (h || 160) - EDGE);
  return {
    left: Math.min(Math.max(EDGE, left), maxLeft),
    top: Math.min(Math.max(EDGE, top), maxTop),
  };
}

// A note parked on the board may never leave the dashed area.
function clampBoard(left, top, w, h) {
  const el = boardEl();
  if (!el) return clampPage(left, top, w, h);
  const r = el.getBoundingClientRect();
  const bx = r.left + window.scrollX;
  const by = r.top + window.scrollY;
  const maxLeft = Math.max(bx, bx + r.width - (w || BOARD_CARD_W));
  // Sideways the note is held inside the dashed area; downwards it is only
  // held at the top edge, because the board grows to cover whatever row the
  // note settles in (see StickyNotesBoard) — so it still never sticks out.
  return {
    left: Math.min(Math.max(bx, left), maxLeft),
    top: Math.max(by, top),
  };
}

const clampFor = (floating, left, top, w, h) =>
  floating ? clampView(left, top, w, h) : clampPage(left, top, w, h);

/**
 * Resolve a note to its on-screen coordinates (clamped).
 * Pinned (anchored): absolute page coords; x/y are px offsets inside the
 * anchor panel, so the note scrolls with that panel and survives reflow.
 * Floating (anchor null): viewport coords; x is a fraction of the window width
 * (0..1) and y is px from the window top, so the note stays on screen.
 */
function notePos(note) {
  const w = note.w || 220;
  const h = note.minimized ? 32 : note.h || 160;
  if (!note.anchor)
    return clampView((note.x || 0) * viewWidth(), note.y || 0, w, h);
  let left;
  let top;
  const el = panelEl(note.anchor);
  if (el) {
    const r = el.getBoundingClientRect();
    left = r.left + window.scrollX + note.x;
    top = r.top + window.scrollY + note.y;
  } else {
    left = 0.62 * docWidth(); // anchor panel hidden → park right
    top = note.y || 0;
  }
  return note.anchor === BOARD_KEY
    ? clampBoard(left, top, w, h)
    : clampPage(left, top, w, h);
}

/** Absolute page coords → stored coords pinned to `anchorKey`. */
function storePinned(left, top, anchorKey) {
  const el = panelEl(anchorKey);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    anchor: anchorKey,
    x: Math.round(left - (r.left + window.scrollX)),
    y: Math.round(top - (r.top + window.scrollY)),
  };
}

/** Viewport coords → stored floating coords. */
function storeFloating(left, top) {
  return {
    anchor: null,
    x: Math.max(0, Math.min(1, left / viewWidth())),
    y: Math.max(0, Math.round(top)),
  };
}

const layoutOf = (note) => ({
  anchor: note.anchor || null,
  x: note.x,
  y: note.y,
  w: note.w,
  h: note.h,
  minimized: !!note.minimized,
});

// Moving onto the board remembers where the note came from (for "Go back");
// moving off it forgets that.
const boardTransition = (note, nextAnchor) => {
  if (nextAnchor === BOARD_KEY && note.anchor !== BOARD_KEY)
    return { boardReturn: JSON.stringify(layoutOf(note)) };
  if (nextAnchor !== BOARD_KEY && note.anchor === BOARD_KEY)
    return { boardReturn: null };
  return {};
};

/** Which labelled panel is under a viewport point (ignoring the notes layer). */
function panelAtPoint(clientX, clientY) {
  if (!document.elementsFromPoint) return null;
  const els = document.elementsFromPoint(clientX, clientY);
  for (const el of els) {
    if (el.closest && el.closest(".xz-notes-layer")) continue;
    const p = el.closest && el.closest("[data-panel-section][data-panel-key]");
    if (p) return p.dataset.panelKey;
  }
  return null;
}

/** Panel whose box is nearest to the note's box (0 distance if overlapping). */
function nearestPanelKey(left, top, w, h) {
  const cx = left + (w || 220) / 2;
  const cy = top + (h || 160) / 2;
  let best = null;
  let bestD = Infinity;
  for (const el of panelEls()) {
    const r = el.getBoundingClientRect();
    const l = r.left + window.scrollX;
    const t = r.top + window.scrollY;
    const dx = cx < l ? l - cx : cx > l + r.width ? cx - (l + r.width) : 0;
    const dy = cy < t ? t - cy : cy > t + r.height ? cy - (t + r.height) : 0;
    const d = Math.hypot(dx, dy);
    if (d < bestD) {
      bestD = d;
      best = el.dataset.panelKey;
    }
  }
  return best;
}

/** Panels the ▲/▼ arrows step between, top to bottom: on screen, expanded, not the board. */
function stepPanels(skipKeys) {
  return panelEls()
    .map((el) => {
      const key = el.dataset.panelKey;
      const r = el.getBoundingClientRect();
      return {
        key,
        label: el.dataset.panelLabel || key,
        left: r.left + window.scrollX,
        top: r.top + window.scrollY,
        width: r.width,
        visible: r.width > 0 && r.height > 0,
      };
    })
    .filter((p) => p.visible && p.key !== BOARD_KEY && !skipKeys.includes(p.key))
    .sort((a, b) => a.top - b.top || a.left - b.left);
}

/**
 * The panel one step above (dir -1) or below (dir 1). A note pinned to a
 * listed panel steps from that panel; any other note steps from where its
 * top edge currently sits on the page.
 */
function stepTarget(dir, anchor, pageTop, skipKeys) {
  const panels = stepPanels(skipKeys);
  const idx = anchor ? panels.findIndex((p) => p.key === anchor) : -1;
  if (idx >= 0) return panels[idx + dir] || null;
  if (dir < 0) {
    const above = panels.filter((p) => p.top < pageTop - 2);
    return above.length ? above[above.length - 1] : null;
  }
  return panels.find((p) => p.top > pageTop + 2) || null;
}

const firstLineOf = (text) =>
  (
    String(text || "")
      .split("\n")
      .find((l) => l.trim()) || "Note"
  )
    .replace(/^#{1,4}\s+|^\s*(?:[-*•]\s+)?(?:\[[ xX]?\]\s?)?|\*\*|__|~~|`/g, "")
    .trim() || "Note";

const TOOLS = [
  {
    id: "bold",
    Icon: Bold,
    title: "Bold (**text**)",
    act: (t, s, e) => wrapSelection(t, s, e, "**"),
  },
  {
    id: "italic",
    Icon: Italic,
    title: "Italic (*text*)",
    act: (t, s, e) => wrapSelection(t, s, e, "*"),
  },
  {
    id: "underline",
    Icon: Underline,
    title: "Underline (__text__)",
    act: (t, s, e) => wrapSelection(t, s, e, "__"),
  },
  {
    id: "strike",
    Icon: Strikethrough,
    title: "Strikethrough (~~text~~)",
    act: (t, s, e) => wrapSelection(t, s, e, "~~"),
  },
  {
    id: "code",
    Icon: Code,
    title: "Code (`text`)",
    act: (t, s, e) => wrapSelection(t, s, e, "`"),
  },
  {
    id: "heading",
    Icon: Heading,
    title: "Heading (# → ## → ###)",
    act: (t, s, e) => applyLinePrefix(t, s, e, "heading"),
  },
  {
    id: "bullet",
    Icon: List,
    title: "Bullet list (- )",
    act: (t, s, e) => applyLinePrefix(t, s, e, "bullet"),
  },
  {
    id: "number",
    Icon: ListOrdered,
    title: "Numbered list (1. )",
    act: (t, s, e) => applyLinePrefix(t, s, e, "number"),
  },
  {
    id: "check",
    Icon: CheckSquare,
    title: "Checkbox ([ ] )",
    act: (t, s, e) => applyLinePrefix(t, s, e, "check"),
  },
];

// ---------------------------------------------------------------------------
// One floating note (desktop layer)
// ---------------------------------------------------------------------------
function Note({
  note,
  maxZ,
  onUpdate,
  onDelete,
  onSendToBoard,
  onReturnFromBoard,
  boardNotes = [],
  skipAnchors = [],
  autoEdit,
  onAutoEditDone,
}) {
  const c = colorOf(note.color);
  const floating = !note.anchor;
  const onBoard = note.anchor === BOARD_KEY;
  const [pos, setPos] = useState(() => notePos(note));
  const [size, setSize] = useState({ w: note.w || 220, h: note.h || 160 });
  const [editing, setEditing] = useState(!!autoEdit);
  const [draft, setDraft] = useState(note.text || "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  // True while the pointer is down: suppresses the layout effects that would
  // otherwise yank the note back to its stored position mid-drag (flicker).
  const [busy, setBusy] = useState(false);
  const taRef = useRef(null);
  const pendingSel = useRef(null);

  // Flag the drag on <body> so the bottom-left controls / panel rail (which sit
  // behind notes and rise on proximity) stay put while a note moves over them.
  useEffect(() => {
    if (!busy) return undefined;
    document.body.classList.add(NOTE_DRAG_CLASS);
    return () => document.body.classList.remove(NOTE_DRAG_CLASS);
  }, [busy]);

  const relayout = useCallback(() => {
    if (dragRef.current || resizeRef.current) return;
    setPos(notePos(note));
  }, [note]);
  useLayoutEffect(() => {
    relayout();
  }, [relayout]);
  useEffect(() => {
    if (resizeRef.current) return;
    setSize({ w: note.w || 220, h: note.h || 160 });
  }, [note.w, note.h]);
  useEffect(() => {
    if (!editing) setDraft(note.text || "");
  }, [note.text, editing]);
  // After every committed move/resize, tell the board so it can grow to fit.
  useLayoutEffect(() => {
    announceGeometry();
  }, [pos, size, note.minimized]);
  useEffect(() => {
    if (autoEdit) setEditing(true);
  }, [autoEdit]);

  // Follow the page layout: window resizes, panels expanding or growing, and
  // the first paints after fonts/images land all move the anchor panel.
  useEffect(() => {
    const onResize = () => relayout();
    window.addEventListener("resize", onResize);
    window.addEventListener(RELAYOUT_EVENT, onResize);
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(onResize)
        : null;
    if (ro) ro.observe(document.body);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener(RELAYOUT_EVENT, onResize);
      if (ro) ro.disconnect();
    };
  }, [relayout]);

  // Restore the caret/selection after a toolbar edit re-renders the textarea.
  useLayoutEffect(() => {
    if (pendingSel.current && taRef.current) {
      const { start, end } = pendingSel.current;
      pendingSel.current = null;
      taRef.current.setSelectionRange(start, end);
    }
  }, [draft]);

  const frontUpdate = () => ((note.z || 0) < maxZ ? { z: maxZ + 1 } : {});

  // ---- drag (header) ----
  const onDragStart = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest("button, input, textarea, select, a")) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      sx: e.clientX,
      sy: e.clientY,
      left: pos.left,
      top: pos.top,
      moved: false,
    };
    setBusy(true);
  };
  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) d.moved = true;
    setPos(
      clampFor(
        floating,
        d.left + dx,
        d.top + dy,
        size.w,
        note.minimized ? 32 : size.h,
      ),
    );
  };
  const onDragEnd = (e) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {
      /* ignore */
    }
    const updates = { ...frontUpdate() };
    if (d.moved) {
      const h = note.minimized ? 32 : size.h;
      const { left, top } = clampFor(
        floating,
        d.left + (e.clientX - d.sx),
        d.top + (e.clientY - d.sy),
        size.w,
        h,
      );
      const dropKey = panelAtPoint(e.clientX, e.clientY);
      if (floating && dropKey !== BOARD_KEY) {
        Object.assign(updates, storeFloating(left, top));
      } else {
        // Pinned: drop onto another panel → re-anchor there, else the nearest.
        // A floating note dropped on the board gets pinned to it.
        const pageLeft = floating ? left + window.scrollX : left;
        const pageTop = floating ? top + window.scrollY : top;
        const anchorKey =
          dropKey ||
          nearestPanelKey(pageLeft, pageTop, size.w, h) ||
          note.anchor;
        const pinned = storePinned(pageLeft, pageTop, anchorKey);
        if (pinned) {
          // On the board the note settles into the nearest free grid cell, so
          // it always ends up inside the dashed area and never on a neighbour.
          const placed =
            anchorKey === BOARD_KEY
              ? snapToBoard(
                  { ...note, w: size.w, h: size.h },
                  boardNotes,
                  undefined,
                  pinned.x,
                  pinned.y,
                )
              : null;
          Object.assign(
            updates,
            pinned,
            placed ? { x: placed.x, y: placed.y, w: placed.w } : {},
            boardTransition(note, anchorKey),
          );
        }
      }
    }
    setBusy(false);
    if (Object.keys(updates).length) onUpdate(note.id, updates);
    else relayout();
  };

  // ---- resize (corner) ----
  const onResizeStart = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    resizeRef.current = { sx: e.clientX, sy: e.clientY, w: size.w, h: size.h };
    setBusy(true);
  };
  const onResizeMove = (e) => {
    const r = resizeRef.current;
    if (!r) return;
    setSize({
      w: Math.max(MIN_W, r.w + (e.clientX - r.sx)),
      h: Math.max(MIN_H, r.h + (e.clientY - r.sy)),
    });
  };
  const onResizeEnd = (e) => {
    const r = resizeRef.current;
    if (!r) return;
    resizeRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {
      /* ignore */
    }
    setBusy(false);
    let w = Math.max(MIN_W, r.w + (e.clientX - r.sx));
    const h = Math.max(MIN_H, r.h + (e.clientY - r.sy));
    if (onBoard) {
      // Keep it inside the board: cap the width, then re-seat the (possibly
      // larger) note in the nearest free cell so it can't cover a neighbour.
      const cols = boardColumns();
      w = Math.min(w, boardMaxNoteW(cols));
      const placed = snapToBoard(
        { ...note, w, h },
        boardNotes,
        cols,
        note.x,
        note.y,
      );
      onUpdate(note.id, {
        w: Math.round(placed.w),
        h: Math.round(h),
        x: placed.x,
        y: placed.y,
        ...frontUpdate(),
      });
      return;
    }
    onUpdate(note.id, { w: Math.round(w), h: Math.round(h), ...frontUpdate() });
  };

  // ---- editing ----
  const commitText = () => {
    setEditing(false);
    if (draft !== (note.text || "")) onUpdate(note.id, { text: draft });
    if (onAutoEditDone) onAutoEditDone(note.id);
  };
  const onEditKey = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      commitText();
      return;
    }
    const mod = e.ctrlKey || e.metaKey;
    if (mod && !e.shiftKey) {
      const k = e.key.toLowerCase();
      const tool =
        k === "b"
          ? "bold"
          : k === "i"
            ? "italic"
            : k === "u"
              ? "underline"
              : null;
      if (tool) {
        e.preventDefault();
        applyTool(TOOLS.find((t) => t.id === tool));
      }
    }
  };
  const applyTool = (tool) => {
    const ta = taRef.current;
    if (!ta || !tool) return;
    const res = tool.act(draft, ta.selectionStart, ta.selectionEnd);
    pendingSel.current = { start: res.start, end: res.end };
    setDraft(res.text);
    ta.focus();
  };
  const toggleCheckbox = (lineIndex) =>
    onUpdate(note.id, { text: toggleCheckboxLine(note.text || "", lineIndex) });

  // ---- pin / unpin ----
  const togglePin = () => {
    const h = note.minimized ? 32 : size.h;
    if (!floating) {
      // Unpin: keep it where it currently sits on screen (pulled into view).
      const v = clampView(
        pos.left - window.scrollX,
        pos.top - window.scrollY,
        size.w,
        h,
      );
      onUpdate(note.id, {
        ...storeFloating(v.left, v.top),
        ...boardTransition(note, null),
      });
    } else {
      const pageLeft = pos.left + window.scrollX;
      const pageTop = pos.top + window.scrollY;
      const key = nearestPanelKey(pageLeft, pageTop, size.w, h);
      const pinned = key && storePinned(pageLeft, pageTop, key);
      if (pinned)
        onUpdate(note.id, { ...pinned, ...boardTransition(note, key) });
    }
  };

  // ---- step to the panel above / below ----
  // Floating notes are in viewport coords, so their page position changes on
  // scroll without a re-render — read it fresh whenever it's needed.
  const pageCoords = () => ({
    left: floating ? pos.left + window.scrollX : pos.left,
    top: floating ? pos.top + window.scrollY : pos.top,
  });
  const canStep = !onBoard && !note.minimized;
  const stepUp =
    canStep && !floating
      ? stepTarget(-1, note.anchor, pos.top, skipAnchors)
      : null;
  const stepDown =
    canStep && !floating
      ? stepTarget(1, note.anchor, pos.top, skipAnchors)
      : null;
  const stepTo = (dir) => {
    const { left: pageLeft, top: pageTop } = pageCoords();
    const target = stepTarget(dir, note.anchor, pageTop, skipAnchors);
    if (!target) return;
    const x = Math.round(
      Math.min(
        Math.max(0, pageLeft - target.left),
        Math.max(0, target.width - size.w),
      ),
    );
    onUpdate(note.id, {
      anchor: target.key,
      x,
      y: EDGE,
      ...boardTransition(note, target.key),
      ...frontUpdate(),
    });
    const noteTop = target.top + EDGE;
    const viewTop = window.scrollY + 72;
    const viewBottom = window.scrollY + window.innerHeight - 24;
    if (noteTop < viewTop || noteTop + Math.min(size.h, 240) > viewBottom)
      window.scrollTo({ top: Math.max(0, noteTop - 96), behavior: "smooth" });
  };

  const bodyH = Math.max(
    40,
    size.h - HEADER_H - FOOTER_H - (editing ? TOOLBAR_H : 0),
  );
  const anchorLabel = floating
    ? "floating"
    : onBoard
      ? "on board"
      : `on ${note.anchor}`;
  const firstLine = firstLineOf(note.text);
  // The note follows the pointer exactly while dragging and is placed in its
  // grid cell the moment it is released — no tweening, so nothing can be left
  // mid-flight (a paused transition used to leave notes off their panel).
  void busy;

  // ---- minimized pill ----
  if (note.minimized) {
    return (
      <div
        className="absolute pointer-events-auto select-none"
        data-note-anchor={note.anchor || ""}
        style={{
          left: pos.left,
          top: pos.top,
          zIndex: 30 + (note.z || 0),
        }}
      >
        <div
          className="flex items-center gap-2 rounded-full pl-2 pr-1 py-1 shadow-lg border cursor-grab active:cursor-grabbing"
          style={{ background: c.bg, borderColor: c.border, color: c.text }}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          title={firstLine}
        >
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ background: c.dot }}
          />
          <span className="text-xs font-semibold max-w-[160px] truncate">
            {firstLine}
          </span>
          <button
            type="button"
            onClick={() =>
              onUpdate(note.id, { minimized: false, ...frontUpdate() })
            }
            className="p-1 rounded-full hover:bg-black/10"
            aria-label="Expand note"
            title="Expand"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute pointer-events-auto select-none"
      data-note-anchor={note.anchor || ""}
      style={{
        left: pos.left,
        top: pos.top,
        width: size.w,
        zIndex: 30 + (note.z || 0),
      }}
      onPointerDown={() => {
        const u = frontUpdate();
        if (u.z) onUpdate(note.id, u);
      }}
    >
      <div
        className="relative rounded-xl shadow-xl border flex flex-col"
        style={{
          background: c.bg,
          borderColor: c.border,
          color: c.text,
          height: size.h,
        }}
      >
        {/* Header: drag handle + controls */}
        <div
          className="flex items-center gap-1 px-2 rounded-t-xl cursor-grab active:cursor-grabbing"
          style={{ background: `${c.border}33`, height: HEADER_H }}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        >
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ background: c.dot }}
          />
          <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70 flex-1 truncate">
            {anchorLabel}
          </span>
          <span className="flex items-center gap-0.5 mr-1" aria-label="Colour">
            {COLOR_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => onUpdate(note.id, { color: k })}
                className={`w-3 h-3 rounded-full border ${note.color === k ? "ring-2 ring-offset-1" : ""}`}
                style={{
                  background: NOTE_COLORS[k].dot,
                  borderColor: NOTE_COLORS[k].border,
                }}
                aria-label={`${k} note`}
                title={k}
              />
            ))}
          </span>
          {canStep && (
            <span className="flex flex-col -my-0.5" aria-label="Move to panel">
              <button
                type="button"
                onClick={() => stepTo(-1)}
                disabled={!floating && !stepUp}
                className="px-0.5 rounded hover:bg-black/10 disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                aria-label="Move to the panel above"
                title={
                  floating
                    ? "Pin to the panel above"
                    : stepUp
                      ? `Move to ${stepUp.label}`
                      : "No panel above"
                }
              >
                <ChevronUp className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => stepTo(1)}
                disabled={!floating && !stepDown}
                className="px-0.5 rounded hover:bg-black/10 disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                aria-label="Move to the panel below"
                title={
                  floating
                    ? "Pin to the panel below"
                    : stepDown
                      ? `Move to ${stepDown.label}`
                      : "No panel below"
                }
              >
                <ChevronDown className="w-3 h-3" />
              </button>
            </span>
          )}
          {!onBoard && onSendToBoard && (
            <button
              type="button"
              onClick={() => onSendToBoard(note.id)}
              className="p-1 rounded hover:bg-black/10"
              aria-label="Send to board"
              title="Send to the Sticky Notes board"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
          )}
          {onBoard && note.boardReturn && onReturnFromBoard && (
            <button
              type="button"
              onClick={() => onReturnFromBoard(note.id)}
              className="p-1 rounded hover:bg-black/10"
              aria-label="Go back to previous position"
              title="Go back to where it was before the board"
            >
              <Undo2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={togglePin}
            className="p-1 rounded hover:bg-black/10"
            aria-label={
              floating ? "Pin to nearest panel" : "Unpin (float on screen)"
            }
            title={
              floating
                ? "Pin — stick to the nearest panel and scroll with the page"
                : "Unpin — float on screen and stay visible while scrolling"
            }
          >
            {floating ? (
              <Pin className="w-3.5 h-3.5" />
            ) : (
              <PinOff className="w-3.5 h-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={() => onUpdate(note.id, { minimized: true })}
            className="p-1 rounded hover:bg-black/10"
            aria-label="Minimize note"
            title="Minimize"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="p-1 rounded hover:bg-black/10"
            aria-label="Delete note"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Formatting toolbar (edit mode). mousedown is prevented so the
            textarea keeps focus — blur would otherwise commit and close. */}
        {editing && (
          <div
            className="flex items-center gap-0.5 px-1.5 border-b overflow-x-auto"
            style={{ height: TOOLBAR_H, borderColor: `${c.border}55` }}
            role="toolbar"
            aria-label="Formatting"
          >
            {TOOLS.map((t) => (
              <button
                key={t.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyTool(t)}
                className="p-1 rounded hover:bg-black/10 flex-shrink-0"
                title={t.title}
                aria-label={t.title}
              >
                <t.Icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        {editing ? (
          <textarea
            ref={taRef}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitText}
            onKeyDown={onEditKey}
            className="w-full bg-transparent outline-none resize-none px-2 py-1.5 text-sm leading-snug font-mono"
            style={{ color: c.text, height: bodyH }}
            placeholder={
              "# Heading\n- bullet · 1. numbered · [ ] task\n**bold** *italic* __underline__ ~~strike~~\nLinks and dd-mm-yyyy dates format automatically"
            }
            aria-label="Note text"
          />
        ) : (
          <div
            className="xz-note-body px-2 py-1.5 text-sm leading-snug break-words overflow-auto cursor-text space-y-0.5"
            style={{ height: bodyH }}
            onDoubleClick={() => setEditing(true)}
            title="Double-click to edit"
          >
            {note.text ? (
              renderNoteMarkdown(note.text, `note-${note.id}`, {
                onToggleCheckbox: toggleCheckbox,
              })
            ) : (
              <span className="opacity-50 italic">Double-click to write…</span>
            )}
          </div>
        )}

        {/* Footer: due date + edit toggle */}
        <div
          className="flex items-center gap-1 px-2 text-[11px] border-t"
          style={{ height: FOOTER_H, borderColor: `${c.border}55` }}
        >
          <Calendar className="w-3 h-3 opacity-70 flex-shrink-0" />
          <input
            type="date"
            value={note.dueDate || ""}
            onChange={(e) =>
              onUpdate(note.id, { dueDate: e.target.value || null })
            }
            className="bg-transparent outline-none text-[11px] w-[112px]"
            style={{ color: c.text }}
            aria-label="Due date"
            title="Due date (shows in Reminders)"
          />
          {note.dueDate && (
            <button
              type="button"
              onClick={() => onUpdate(note.id, { dueDate: null })}
              className="p-0.5 rounded hover:bg-black/10"
              aria-label="Clear due date"
            >
              <X className="w-3 h-3" />
            </button>
          )}
          <span className="flex-1" />
          {editing ? (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={commitText}
              className="font-semibold px-1.5 py-0.5 rounded hover:bg-black/10"
            >
              Done
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="opacity-70 hover:opacity-100 underline px-1"
            >
              Edit
            </button>
          )}
        </div>

        {/* Resize handle */}
        <div
          onPointerDown={onResizeStart}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeEnd}
          onPointerCancel={onResizeEnd}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          style={{
            background: `linear-gradient(135deg, transparent 50%, ${c.border} 50%)`,
            borderBottomRightRadius: 12,
          }}
          aria-hidden="true"
        />

        {confirmDelete && (
          <div
            className="absolute inset-0 rounded-xl flex flex-col items-center justify-center gap-2 text-sm"
            style={{ background: `${c.bg}F2`, color: c.text }}
            role="alertdialog"
            aria-label="Delete this note?"
          >
            <span className="font-semibold">Delete this note?</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onDelete(note.id)}
                className="px-3 py-1 rounded-lg bg-red-500 text-white font-semibold"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="px-3 py-1 rounded-lg border font-semibold"
                style={{ borderColor: c.border }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Desktop layer: floating notes + bottom-left controls
// ---------------------------------------------------------------------------
export function StickyNotesLayer({
  notes,
  hidden,
  onToggleHidden,
  onAdd,
  onUpdate,
  onDelete,
  canGather = false,
  canRestore = false,
  onGather,
  onRestore,
  onSendToBoard,
  onReturnFromBoard,
  hiddenAnchors = [],
}) {
  const [autoEditId, setAutoEditId] = useState(null);
  // Bottom-left controls live BEHIND the note layers and rise above them only
  // while the pointer is near that corner (or a button has focus).
  const controlsRef = useRef(null);
  const controlsRaised = useProximityRaise(controlsRef, 28);
  const list = Array.isArray(notes) ? notes : [];
  // Notes pinned to a minimised panel stay out of the way until it expands.
  const visible = list.filter(
    (n) => !n.anchor || !hiddenAnchors.includes(n.anchor),
  );
  const pinned = visible.filter((n) => n.anchor);
  const floating = visible.filter((n) => !n.anchor);
  const maxZ = list.reduce((m, n) => Math.max(m, n.z || 0), 0);
  // Every note currently parked on the board — used to keep board notes from
  // landing on top of one another.
  const boardOccupants = list.filter((n) => n.anchor === BOARD_KEY);

  // Watch every panel: when one moves or changes size, pinned notes re-seat
  // themselves on their anchor. (Panels are re-queried on each mutation so
  // newly rendered panels are picked up too.)
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => requestRelayout());
    const observe = () => {
      ro.disconnect();
      ro.observe(document.body);
      panelEls().forEach((el) => ro.observe(el));
      requestRelayout();
    };
    observe();
    const mo =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(observe)
        : null;
    const main = document.querySelector("main");
    if (mo && main) mo.observe(main, { childList: true, subtree: false });
    const settle = [80, 300, 900].map((ms) => setTimeout(requestRelayout, ms));
    return () => {
      ro.disconnect();
      if (mo) mo.disconnect();
      settle.forEach(clearTimeout);
    };
  }, []);

  const handleAdd = () => {
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const { left, top } = clampPage(
      cx + window.scrollX - 110,
      cy + window.scrollY - 80,
      220,
      160,
    );
    const anchorKey =
      panelAtPoint(cx, cy) || nearestPanelKey(left, top, 220, 160);
    const at =
      (anchorKey && storePinned(left, top, anchorKey)) ||
      storeFloating(left - window.scrollX, top - window.scrollY);
    const id = onAdd({ ...at, z: maxZ + 1 });
    setAutoEditId(id);
    if (hidden && onToggleHidden) onToggleHidden(false);
  };

  const renderNote = (n) => (
    <Note
      key={n.id}
      note={n}
      maxZ={maxZ}
      onUpdate={onUpdate}
      onDelete={onDelete}
      onSendToBoard={onSendToBoard}
      onReturnFromBoard={onReturnFromBoard}
      boardNotes={boardOccupants}
      skipAnchors={hiddenAnchors}
      autoEdit={autoEditId === n.id}
      onAutoEditDone={() => setAutoEditId(null)}
    />
  );

  return (
    <>
      {!hidden && (
        <div
          className="xz-notes-layer hidden md:block absolute inset-x-0 top-0 h-0 z-30 pointer-events-none"
          aria-label="Sticky notes"
        >
          {pinned.map(renderNote)}
        </div>
      )}
      {!hidden && floating.length > 0 && (
        <div
          className="xz-notes-layer hidden md:block fixed inset-0 z-[35] pointer-events-none"
          aria-label="Floating sticky notes"
        >
          {floating.map(renderNote)}
        </div>
      )}

      <div
        ref={controlsRef}
        className={`xz-notes-controls hidden md:flex fixed left-3 bottom-4 flex-col gap-2 transition-opacity duration-200 ${
          controlsRaised ? "z-[45] opacity-100" : "z-20 opacity-70"
        }`}
      >
        <button
          type="button"
          onClick={handleAdd}
          className="tap glass rounded-full p-3 text-yellow-300 card-shadow hover:scale-105 transition-transform"
          aria-label="New sticky note"
          title="New sticky note"
        >
          <Plus className="w-5 h-5" />
        </button>
        {list.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => onToggleHidden && onToggleHidden(!hidden)}
              className="tap glass rounded-full p-3 text-cyan-300 card-shadow"
              aria-label={hidden ? "Show sticky notes" : "Hide sticky notes"}
              title={
                hidden
                  ? `Show ${list.length} sticky note(s)`
                  : "Hide sticky notes"
              }
            >
              {hidden ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
            {canGather && (
              <button
                type="button"
                onClick={() => {
                  if (onToggleHidden) onToggleHidden(false);
                  if (onGather) onGather();
                }}
                className="tap glass rounded-full p-3 text-purple-300 card-shadow"
                aria-label="Gather all sticky notes"
                title="Gather all — send every note to the Sticky Notes board"
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
            )}
            {canRestore && (
              <button
                type="button"
                onClick={() => {
                  if (onToggleHidden) onToggleHidden(false);
                  if (onRestore) onRestore();
                }}
                className="tap glass rounded-full p-3 text-purple-300 card-shadow"
                aria-label="Restore all sticky notes"
                title="Restore all — send every board note back where it was"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Mobile panel: notes as a simple list (no free positioning on touch)
// ---------------------------------------------------------------------------
export function StickyNotesPanel({ notes, onAdd, onUpdate, onDelete }) {
  const list = Array.isArray(notes) ? notes : [];
  const [confirmId, setConfirmId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const maxZ = list.reduce((m, n) => Math.max(m, n.z || 0), 0);

  return (
    <section className="xz-hide-in-focus md:hidden bg-gray-800 rounded-3xl p-4 border-2 border-yellow-500/30 shadow-lg card-shadow mb-6">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-lg font-bold text-yellow-400 flex items-center gap-2">
          <StickyIcon className="w-5 h-5" />
          Sticky Notes
        </h3>
        <button
          type="button"
          onClick={() => {
            const id = onAdd({ anchor: null, x: 0.5, y: 0, z: maxZ + 1 });
            setEditingId(id);
          }}
          className="tap px-3 py-2 rounded-2xl bg-yellow-400 hover:bg-yellow-300 text-black text-sm font-semibold flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          New
        </button>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-gray-400">
          No notes yet. On larger screens notes float over the page and can be
          pinned to any panel.
        </p>
      ) : (
        <div className="space-y-3">
          {list.map((n) => {
            const c = colorOf(n.color);
            const isEditing = editingId === n.id;
            return (
              <div
                key={n.id}
                className="rounded-xl border p-2 shadow"
                style={{
                  background: c.bg,
                  borderColor: c.border,
                  color: c.text,
                }}
              >
                <div className="flex items-center gap-1 mb-1">
                  {COLOR_KEYS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => onUpdate(n.id, { color: k })}
                      className={`w-4 h-4 rounded-full border ${n.color === k ? "ring-2 ring-offset-1" : ""}`}
                      style={{
                        background: NOTE_COLORS[k].dot,
                        borderColor: NOTE_COLORS[k].border,
                      }}
                      aria-label={`${k} note`}
                    />
                  ))}
                  <span className="flex-1" />
                  <button
                    type="button"
                    onClick={() => setEditingId(isEditing ? null : n.id)}
                    className="tap px-2 py-1 rounded text-xs font-semibold hover:bg-black/10"
                  >
                    {isEditing ? "Done" : "Edit"}
                  </button>
                  {confirmId === n.id ? (
                    <span className="flex items-center gap-1 text-xs">
                      <button
                        type="button"
                        onClick={() => onDelete(n.id)}
                        className="px-2 py-1 rounded bg-red-500 text-white font-semibold"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmId(null)}
                        className="px-2 py-1 rounded border"
                        style={{ borderColor: c.border }}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmId(n.id)}
                      className="tap p-1 rounded hover:bg-black/10"
                      aria-label="Delete note"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {isEditing ? (
                  <textarea
                    autoFocus
                    value={n.text || ""}
                    onChange={(e) => onUpdate(n.id, { text: e.target.value })}
                    rows={4}
                    className="w-full bg-transparent outline-none resize-y text-sm leading-snug font-mono"
                    style={{ color: c.text }}
                    placeholder={"# Heading · - bullet · [ ] task · **bold**"}
                    aria-label="Note text"
                  />
                ) : (
                  <div
                    className="text-sm leading-snug break-words space-y-0.5 min-h-[2rem]"
                    onClick={() => !n.text && setEditingId(n.id)}
                  >
                    {n.text ? (
                      renderNoteMarkdown(n.text, `mnote-${n.id}`, {
                        onToggleCheckbox: (li) =>
                          onUpdate(n.id, {
                            text: toggleCheckboxLine(n.text || "", li),
                          }),
                      })
                    ) : (
                      <span className="opacity-50 italic">Tap to write…</span>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-1 text-[11px] mt-1">
                  <Calendar className="w-3 h-3 opacity-70" />
                  <input
                    type="date"
                    value={n.dueDate || ""}
                    onChange={(e) =>
                      onUpdate(n.id, { dueDate: e.target.value || null })
                    }
                    className="bg-transparent outline-none text-[11px]"
                    style={{ color: c.text }}
                    aria-label="Due date"
                  />
                  {n.dueDate && (
                    <button
                      type="button"
                      onClick={() => onUpdate(n.id, { dueDate: null })}
                      className="p-0.5"
                      aria-label="Clear due date"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
